// Hooks
import { defineStore } from "pinia";
import { ref, inject, watch, computed } from "vue";

import { PARTNERS_DOMAINS } from "@/constants";
import initWidget from "@/helpers/initWidget";
import { useAnalyticsEventTiModel } from "@/hooks/useAnalytics/useAnalytics";
// Stores
import useStoreResetter from "@/hooks/useStoreResetter/useStoreResetter";

// Types
import type { I_TIW, I_INJECTED_TIW, I_CUSTOM_STYLES } from "./tiw.types";

/**
 * * 🆂🆃🅾🆁🅴 [TIW STORE] Самый высокоуровневый STORE приложения
 * */
export const useTIWStore = defineStore("TIW", () => {
    // |---------------|--------------------|
    // | INJECTED DATA | APP INITIALIZATION |
    // |---------------|--------------------|

    /**
     * Инициализированный класс TradeInWidget
     */
    const INJECTED_TIW = inject<I_INJECTED_TIW>("widget");

    /**
     * Инициализированный instance текущего приложения
     */
    // const INJECTED_APP = inject("appInstance");

    // |-------|-----------------------------------------------------------------|
    // | STATE | ⓘ См. https://pinia-ru.netlify.app/core-concepts/#setup-stores |
    // |-------|-----------------------------------------------------------------|

    /** Предыдущий шаг формы */
    const PREVIOUS_STEP = ref<I_TIW["PREVIOUS_STEP"]>(null);

    /** Текущий шаг формы */
    const CURRENT_STEP = ref<I_TIW["CURRENT_STEP"]>("chooseDevice");

    /** Закрыть / Открыть виджет / true - закрыт */
    const IS_CLOSE = ref<I_TIW["IS_CLOSE"]>(true);

    /** Состояние действия анимации Закрытия */
    const IS_TRANSITION = ref<I_TIW["IS_TRANSITION"]>(false);

    /** Свернуть / Показать виджет / true - свернут  */
    const IS_ROLL = ref<I_TIW["IS_ROLL"]>(false);

    /**  Данные виджета (переводы, доступные локали и т.д.) */
    const WIDGET_DATA = ref<I_TIW["WIDGET_DATA"]>(null);

    /**  Загрузка данных виджета */
    const IS_INIT_WIDGET_LOADING = ref<boolean>(true);

    const DISCOUNT_LATER = ref<boolean>(false);

    const REASONS_TO_CANCEL = ref<I_TIW["REASONS_TO_CANCEL"]>([]);

    // |----------|
    // | COMPUTED |
    // |----------|

    const isDiscountLater = computed<boolean>({
        get() {
            return DISCOUNT_LATER.value;
        },
        set(value) {
            DISCOUNT_LATER.value = value;
        },
    });

    const domainIsComfy = computed(() => {
        return INJECTED_TIW.current_settings.domain === PARTNERS_DOMAINS.COMFY;
    });
    const domainIsAvoAfrica = computed(() => {
        return INJECTED_TIW.current_settings.domain === PARTNERS_DOMAINS.AVO_AFRICA;
    });
    const domainIsTakealot = computed(() => {
        return INJECTED_TIW.current_settings.domain === PARTNERS_DOMAINS.TAKEALOT;
    });
    const domainIsIspaceMD = computed(() => {
        return INJECTED_TIW.current_settings.domain === PARTNERS_DOMAINS.ISPACE_MD;
    });
    const domainIsOrangeMD = computed(() => {
        return INJECTED_TIW.current_settings.domain === PARTNERS_DOMAINS.ORANGE_MD;
    });

    const locationIsUA = computed<boolean>(() => {
        const _location: string = String(WIDGET_DATA.value.location).toLowerCase();

        return _location === "ua";
    });

    const locationIsZA = computed<boolean>(() => {
        const _location: string = String(WIDGET_DATA.value.location).toLowerCase();

        return _location === "za";
    });

    const locationIsMD = computed<boolean>(() => {
        const _location: string = String(WIDGET_DATA.value.location).toLowerCase();

        return _location === "md";
    });

    const locationIsGE = computed<boolean>(() => {
        const _location: string = String(WIDGET_DATA.value.location).toLowerCase();

        return _location === "ge";
    });

    const phoneMask = computed<string>(() => {
        const _location: string = String(WIDGET_DATA.value.location).toLowerCase();

        if (_location === "ua") {
            return "+38 (___) ___-__-__";
        }

        if (_location === "kz") {
            return "+7 (___) ___-__-__";
        }

        if (_location === "ge") {
            return "+995 ___-__-__-__";
        }

        /*
        if (_location === "pl") {
            return "";
        }
        //*/

        if (_location === "md") {
            return "+373 __ ___ ___";
        }

        if (_location === "az") {
            return "+994 __ ___ __ __";
        }

        if (_location === "za") {
            return "+27 (A_) ___-__-__";
        }

        return "+7 (___) ___-__-__";
    });

    const phoneMaskPlaceholder = computed(() => phoneMask.value.replace(/[A-Za-z]/g, "_"));

    const passportIssueDateMask = computed<string>(() => {
        return "__.__.____";
    });

    const seriasAndNumberMDMask = computed<string[]>(() => {
        return ["Z________", "ZZ_______", "Z_______"];
    });

    const showHelpCrunchChat = computed<boolean>(
        (): boolean => Number(WIDGET_DATA.value?.data?.chat || 0) === 1,
    );

    const standardizedLocale = computed<string>((): string => {
        let locale: string = INJECTED_TIW.current_settings.locale;

        const standardLocales: Record<string, string> = {
            kz: "kk",
            ua: "uk",
            ge: "ka",
        };

        if (locale in standardLocales) {
            locale = standardLocales[locale];
        }

        return locale;
    });

    const minOrderPrice = computed<number>((): number => {
        return Number(WIDGET_DATA.value.data.min_order || "0");
    });

    const modeIsEmbed = computed<boolean>(
        (): boolean => INJECTED_TIW.current_settings?.mode === "embed",
    );
    const modeIsPopup = computed<boolean>((): boolean => !modeIsEmbed.value);
    const isPickupOnly = computed<boolean>(
        (): boolean => Number(WIDGET_DATA.value.data.pickup_only) === 1,
    );
    const hasDiscountLater = computed<boolean>(
        () => Number(WIDGET_DATA.value.data.get_discount_later) === 1,
    );
    const isFirstStep = computed(() => CURRENT_STEP.value === "chooseDevice");

    /** Выкупаемое устройство */
    const INJECTED_GOOD = ref<I_TIW["INJECTED_GOOD"]>(null);

    const customStyles = computed<I_CUSTOM_STYLES>(() => {
        // for debug
        // https://fonts.googleapis.com/css2?family=Golos+Text:wght@900&display=swap
        // https://cdn.comfy.ua/media/im/fonts/PTRootUI/regular.woff2

        const styles: I_CUSTOM_STYLES = {
            font: {
                family: WIDGET_DATA.value?.data?.font_family ?? "",
                link: "", // WIDGET_DATA.value.data.font_link
                google: "", // WIDGET_DATA.value.data.font_link
                size: WIDGET_DATA.value?.data?.font_size ?? "",
            },
            color: {
                main: WIDGET_DATA.value?.data?.main_color ?? "",
                text: WIDGET_DATA.value?.data?.text_color ?? "",
            },
        };

        const link: string = String(WIDGET_DATA.value?.data?.font_link ?? "");

        if (link) {
            if (/fonts\.googleapis\.com/.test(WIDGET_DATA.value?.data?.font_link ?? "")) {
                styles.font.google = WIDGET_DATA.value?.data?.font_link ?? "";
            } else if (WIDGET_DATA.value?.data?.font_link ?? "") {
                styles.font.link = WIDGET_DATA.value?.data?.font_link ?? "";
                styles.font.family = "TIW-FONT";
            }
        }

        return styles;
    });

    // |---------|
    // | METHODS |
    // |---------|

    /** Проверка на текущий шаг */
    const isCurrentStep = (step: string): boolean => {
        if (IS_INIT_WIDGET_LOADING.value) return false;
        return CURRENT_STEP.value === step;
    };

    /** Переключить экраны (шаги форм) виджета */
    const switchStep = (toStep: I_TIW["CURRENT_STEP"]) => {
        if (toStep === CURRENT_STEP.value) return;

        if (toStep === "chooseDevice") {
            PREVIOUS_STEP.value = null;
            CURRENT_STEP.value = toStep;
            return;
        }

        PREVIOUS_STEP.value = CURRENT_STEP.value;
        CURRENT_STEP.value = toStep;
    };

    const backToPrevStep = () => {
        switchStep(PREVIOUS_STEP.value ?? "chooseDevice");
    };

    /** ЗАКРЫТЬ модальное окно виджета */
    const closeWidget = () => {
        if (IS_ROLL.value) return;

        IS_TRANSITION.value = true;
        setTimeout(() => {
            IS_TRANSITION.value = false;
        }, 150);

        // Reset
        if (modeIsPopup.value) IS_CLOSE.value = true;
        INJECTED_GOOD.value = null;
        CURRENT_STEP.value = "chooseDevice";
        PREVIOUS_STEP.value = null;
    };

    /** ОТКРЫТЬ модальное окно виджета */
    const openWidget = () => {
        IS_TRANSITION.value = true;
        setTimeout(() => {
            IS_TRANSITION.value = false;
        }, 150);
        IS_CLOSE.value = false;
    };

    /** СВЕРНУТЬ модальное окно виджета */
    const rollDownWidget = () => {
        IS_ROLL.value = true;
    };

    /** РАЗВЕРНУТЬ модальное окно виджета */
    const rollUpWidget = () => {
        IS_ROLL.value = false;
    };

    // |----------|
    // | WATCHERS |
    // |----------|

    if (modeIsPopup.value) {
        watch(IS_CLOSE, () => {
            document.body.style.overflow = !IS_CLOSE.value ? "hidden" : "initial";
        });

        watch(IS_ROLL, () => {
            document.body.style.overflow = IS_ROLL.value ? "initial" : "hidden";
        });
    }

    // |---------------|---------------------------------|
    // | INJECTED DATA | APP ASSIGNMENT EXTERNAL METHODS |
    // |---------------|---------------------------------|

    /** Сбросить состояние и открыть виджет на первом шаге */
    const startTradeIn = (injectedGood: I_TIW["INJECTED_GOOD"]) => {
        useStoreResetter();
        switchStep("chooseDevice");

        INJECTED_GOOD.value = injectedGood;

        if (IS_INIT_WIDGET_LOADING.value) {
            initWidget().then(() => {
                IS_INIT_WIDGET_LOADING.value = false;
                useAnalyticsEventTiModel();
            });
        } else {
            useAnalyticsEventTiModel();
        }

        openWidget();
    };

    const initTradeInWithGood = () => {
        console.log("INJECTED_TIW", INJECTED_TIW);

        // Открыть виджет с покупаемым устройством
        INJECTED_TIW.tradeInWithGood = (calculatedDeviceData) => {
            startTradeIn({
                article: calculatedDeviceData.article.toString(),
                panelTitle: calculatedDeviceData.name,
                panelPrice: calculatedDeviceData.price,
                panelDevicePicture: calculatedDeviceData.image,
            });
        };

        // Открыть виджет без покупаемого устройства (только сдача)
        INJECTED_TIW.tradeIn = () => {
            startTradeIn(null);
        };
    };

    return {
        // * 🆂🆃🅾🆁🅴
        PREVIOUS_STEP,
        CURRENT_STEP,
        IS_ROLL,
        IS_CLOSE,
        IS_TRANSITION,
        WIDGET_DATA,
        IS_INIT_WIDGET_LOADING,
        DISCOUNT_LATER,
        REASONS_TO_CANCEL,

        // * 🅲🅾🅼🅿🆄🆃🅴🅳
        isFirstStep,
        domainIsComfy,
        domainIsAvoAfrica,
        domainIsTakealot,
        domainIsIspaceMD,
        domainIsOrangeMD,
        locationIsUA,
        locationIsZA,
        locationIsMD,
        locationIsGE,
        phoneMask,
        phoneMaskPlaceholder,
        passportIssueDateMask,
        seriasAndNumberMDMask,
        showHelpCrunchChat,
        standardizedLocale,
        minOrderPrice,
        modeIsEmbed,
        modeIsPopup,
        isPickupOnly,
        customStyles,
        isDiscountLater,
        hasDiscountLater,

        // * 🅸🅽🅹🅴🅲🆃🅴🅳
        INJECTED_TIW,
        INJECTED_GOOD,

        // * 🅼🅴🆃🅷🅾🅳🆂
        initTradeInWithGood,
        isCurrentStep,
        switchStep,
        backToPrevStep,
        openWidget,
        closeWidget,
        rollUpWidget,
        rollDownWidget,
    };
});
